import { useCallback, useEffect, useRef, useState } from 'react';

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: string | HTMLElement,
        options: {
          sitekey: string;
          callback: (token: string) => void;
          'expired-callback'?: () => void;
          'error-callback'?: () => void;
          size?: 'normal' | 'compact' | 'invisible';
          theme?: 'light' | 'dark' | 'auto';
        },
      ) => string;
      /** Required for `size: 'invisible'` — token is only issued after this runs. */
      execute: (widgetIdOrContainer?: string | HTMLElement) => void;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;

/**
 * Invisible Cloudflare Turnstile CAPTCHA.
 *
 * For `size: 'invisible'`, Cloudflare does **not** issue a token on page load — you must call
 * `executeTurnstile()` when the user submits (e.g. confirm OTP). Use the returned token in the API body.
 */
export function useTurnstile() {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  /** Set by `executeTurnstile` while waiting for Cloudflare callback (or timeout). */
  const tokenPendingRef = useRef<((t: string | null) => void) | null>(null);

  useEffect(() => {
    if (!SITE_KEY || !containerRef.current) return;

    const finishToken = (t: string | null) => {
      setToken(t);
      const pending = tokenPendingRef.current;
      tokenPendingRef.current = null;
      pending?.(t);
    };

    const tryRender = () => {
      if (!window.turnstile || !containerRef.current) return;
      if (widgetIdRef.current !== null) return;

      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: SITE_KEY,
        callback: (t: string) => finishToken(t),
        'expired-callback': () => finishToken(null),
        'error-callback': () => finishToken(null),
        size: 'invisible',
        theme: 'auto',
      });
    };

    if (window.turnstile) {
      tryRender();
    } else {
      const interval = setInterval(() => {
        if (window.turnstile) {
          clearInterval(interval);
          tryRender();
        }
      }, 200);
      return () => clearInterval(interval);
    }
  }, []);

  const resetTurnstile = useCallback(() => {
    setToken(null);
    if (widgetIdRef.current !== null && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current);
    }
  }, []);

  /**
   * Runs the invisible challenge and resolves when a token is ready (or null on failure / timeout).
   */
  const executeTurnstile = useCallback((): Promise<string | null> => {
    if (!SITE_KEY || widgetIdRef.current === null || !window.turnstile?.execute) {
      return Promise.resolve(null);
    }
    return new Promise((resolve) => {
      const tid = window.setTimeout(() => {
        tokenPendingRef.current = null;
        resolve(null);
      }, 25_000);

      const done = (t: string | null) => {
        window.clearTimeout(tid);
        resolve(t);
      };

      tokenPendingRef.current = done;
      try {
        window.turnstile!.execute(widgetIdRef.current!);
      } catch {
        window.clearTimeout(tid);
        tokenPendingRef.current = null;
        resolve(null);
      }
    });
  }, []);

  return {
    token,
    resetTurnstile,
    executeTurnstile,
    turnstileRef: containerRef,
    turnstileEnabled: !!SITE_KEY,
  };
}
